import {
  BOUND_OF_SEARCH_AUTOCOMPLETE,
  COUNT_OF_QUERY,
  StateOfAutocomplete,
} from '@/constants/components/autocomplete'
import { STATE_MENU_ADDRESS_SYSTEM } from '@/constants/statesOfAddressSystem'

import {
  useAddressSystemMapStore,
  useAddressSystemStateStore,
  useAddressSystemStore,
} from '@/store/addressSystem'
import { useCityStore } from '@/store/city/city'
import { PlacesSearch } from '@/store/delivery/delivery.types'

import { NotFoundError, ServerError, Success } from '@/api/results'
import { StateGeoAddress } from '@/types/mapServices'

import type { LatLng } from '@/api/modules/delivery/types'
import type { SuggestedOfAddress } from '@/api/modules/geo/geocode.types'

export const useAutocompleteAddress = () => {
  const cityStore = useCityStore()

  const cityInfo = computed(() => {
    return {
      countryName: cityStore.cityInfo.country?.name || '',
      cityName: cityStore.cityName || '',
      codeRegion: String(cityStore.cityState?.code) || '',
      nameRegion: cityStore.cityState?.name || '',
    }
  })

  // Новый fetch для автокомплита
  const fetchFromNewSuggestions = async (request: string) => {
    const { $apiService } = useNuxtApp()

    const result = await $apiService.geocode.getSuggestedSearchesOfAddress(
      request,
      BOUND_OF_SEARCH_AUTOCOMPLETE.HOUSE,
      cityInfo.value.countryName,
      cityInfo.value.codeRegion,
      cityInfo.value.cityName,
    )

    if (!(result instanceof Success)) {
      searchSuggestionsError.value = result.message

      return
    }

    // Если нет подсказок
    if (!result.data.length) {
      searchSuggestionsError.value = StateOfAutocomplete.NOT_FOUND

      return
    }

    searchSuggestionsError.value = ''

    return result.data
  }

  const searchSuggestionsError = ref('')
  const searchSuggestionsLoading = ref(false)

  // Поиск совпадений по адресу
  const searchSuggestions = async (request: string | null) => {
    if (!isRequestValid(request) || !request) {
      searchSuggestionsError.value = StateOfAutocomplete.EMPTY

      return []
    }

    searchSuggestionsLoading.value = true

    // В зависимости от тагла определяем вызов
    const result = await fetchFromNewSuggestions(request)

    searchSuggestionsLoading.value = false

    return result || []
  }

  const isRequestValid = (request: string | null) => {
    return request && request.length >= COUNT_OF_QUERY
  }

  const addressSystemStore = useAddressSystemStore()
  const { setSelectedAddress, setDeliveryAddress, resetDeliveryAddress } =
    useAddressSystemStore()
  const { changeMenuState } = useAddressSystemStateStore()
  const { toggleLoadingAreas, changeVisibleClarifyShow } =
    useAddressSystemMapStore()

  const getAddressInformation = async (data: {
    selectedSuggestion: SuggestedOfAddress
    coordinates: LatLng | null
  }) => {
    if (!data.coordinates) return

    // Фиксируем выбранный адрес с координатами
    setSelectedAddress({
      address: data.selectedSuggestion.value,
      coordinates: data.coordinates,
    })

    // Изменяем лоадинг
    changeMenuState(STATE_MENU_ADDRESS_SYSTEM.LOADING)
    addressSystemStore.setDeliveryAddressState(StateGeoAddress.LOADING)
    toggleLoadingAreas()

    const { $apiService, $metrics, $sentryCaptureException } = useNuxtApp()

    const addressInfo = await $apiService.geocode.getAddressInfoByCoordinates(
      data.coordinates,
    )

    // В случае 404, то адрес вне зоны доставки
    if (addressInfo instanceof NotFoundError) {
      changeMenuState(STATE_MENU_ADDRESS_SYSTEM.NOT_DELIVERY)
      toggleLoadingAreas()

      $metrics?.eventSendGoal({ ym: 'address_is_not_in_delivery_area' })

      const deliveryAddressData = {
        address: {
          place: PlacesSearch.AUTOCOMPLETE,
          address: data.selectedSuggestion,
        },
        coordinates: data.coordinates,
        state: StateGeoAddress.READY, // Изменяем лоадинг балуна (setDeliveryAddressState)
        details: null,
      }
      setDeliveryAddress(deliveryAddressData)
      changeVisibleClarifyShow(true)

      return
    }

    // В случае иной ошибки, отображаем состояние ошибки
    if (addressInfo instanceof ServerError) {
      changeMenuState(STATE_MENU_ADDRESS_SYSTEM.ERROR)
      addressSystemStore.setDeliveryAddressState(StateGeoAddress.READY)
      toggleLoadingAreas()

      return
    }

    const { street, house, value } = data.selectedSuggestion

    // В случае если адрес в зоне доставки, и адрес имеет номер дома, но нет улицы
    if (!street && house && addressInfo) {
      $sentryCaptureException(`Некорректный адрес: ${value}`, {
        extra: {
          address: data.selectedSuggestion,
          coordinates: data.coordinates,
        },
        level: 'warning',
      })
    }

    const deliveryAddressData = {
      address: {
        place: PlacesSearch.AUTOCOMPLETE,
        address: data.selectedSuggestion,
      },
      coordinates: data.coordinates,
      state: StateGeoAddress.READY, // Изменяем лоадинг балуна (setDeliveryAddressState)
      details: addressInfo.data,
    }

    setDeliveryAddress(deliveryAddressData)

    toggleLoadingAreas()
    changeMenuState(STATE_MENU_ADDRESS_SYSTEM.SELECTED)
    changeVisibleClarifyShow(true)
  }

  const getCoordinatesByAddress = async (
    selectedSuggestion: SuggestedOfAddress,
    geoCode?: string,
  ): Promise<LatLng | undefined> => {
    const body =
      selectedSuggestion.unrestricted_value || selectedSuggestion.value

    const { $apiService } = useNuxtApp()

    const coords = await $apiService.geocode.getCoordinatesByAddress(
      body,
      geoCode,
    )

    // Изменяем состояние автокомплита в случае ошибки определения координат адреса
    if (!(coords instanceof Success)) {
      changeMenuState(STATE_MENU_ADDRESS_SYSTEM.ERROR)

      resetDeliveryAddress(PlacesSearch.AUTOCOMPLETE) // Убираем балун

      return
    }

    return [coords.data.longitude, coords.data.latitude]
  }

  return {
    searchSuggestions,
    isRequestValid,
    searchSuggestionsError,
    searchSuggestionsLoading,
    getAddressInformation,
    getCoordinatesByAddress,
  }
}
